/**
 * Position requests in plain words — shared by HR Ops' Position requests page,
 * the New position dialog and Role Master's Requests tab.
 *
 * Pure: no React, no API. Tested from apps/hr-ops
 * (src/pages/positionRequestStatus.test.ts).
 */
import type {
  ExistingSeat,
  ExistingSeatsResult,
  PositionRequestOrigin,
  PositionRequestStage,
  TrackedPositionRequest,
} from '../api/services/positionRequestService';

export type Tone = 'amber' | 'blue' | 'sky' | 'red' | 'emerald' | 'gray';

export const STAGE_META: Record<PositionRequestStage, { label: string; tone: Tone; blurb: string }> = {
  awaiting_funding: {
    label: 'Waiting for budget',
    tone: 'amber',
    blurb: 'Role Master has to link a budget line to it, or sign it off without one, before it can be approved.',
  },
  awaiting_approval: {
    label: 'Waiting for approval',
    tone: 'blue',
    blurb: 'The budget is settled. Role Master still has to approve it.',
  },
  creating: {
    label: 'Being created',
    tone: 'sky',
    blurb: 'Approved. The position is being created in F&O now.',
  },
  needs_attention: {
    label: 'Creation stopped',
    tone: 'red',
    blurb: 'Creating the position in F&O stopped partway. Role Master has to fix the cause and approve it again.',
  },
  created: {
    label: 'Position ready',
    tone: 'emerald',
    blurb: 'The position exists in F&O. Select it to finish the hire or change it was for.',
  },
  position_reserved: {
    label: 'Vacancy reserved',
    tone: 'emerald',
    blurb: 'The hiring manager chose a vacant position with budget. It is held for this hire, so recruitment can start.',
  },
  reserved_no_budget: {
    label: 'Waiting for budget',
    tone: 'amber',
    blurb: 'The vacant position chosen has no budget for the year the hire starts. Finance has to put budget on it before recruitment starts.',
  },
  filled: {
    label: 'Filled',
    tone: 'emerald',
    blurb: 'The position exists and someone is in it.',
  },
  rejected: {
    label: 'Rejected',
    tone: 'red',
    blurb: 'Role Master rejected the request.',
  },
  withdrawn: {
    label: 'Withdrawn',
    tone: 'gray',
    blurb: 'The request was withdrawn before a decision.',
  },
};

export function stageMeta(stage: string): { label: string; tone: Tone; blurb: string } {
  return STAGE_META[stage as PositionRequestStage]
    || { label: stage || 'Unknown', tone: 'gray', blurb: '' };
}

export type StepState = 'done' | 'current' | 'todo' | 'blocked' | 'failed' | 'skipped';
export interface Step { key: 'raised' | 'budget' | 'approval' | 'created' | 'filled'; label: string; state: StepState }

const STEP_LABELS: Array<[Step['key'], string]> = [
  ['raised', 'Raised'],
  ['budget', 'Budget'],
  ['approval', 'Approval'],
  ['created', 'Created in F&O'],
  ['filled', 'Filled'],
];

/** The five steps a request goes through, and where this one is. */
export function stageSteps(stage: string): Step[] {
  const build = (states: StepState[]): Step[] =>
    STEP_LABELS.map(([key, label], i) => ({ key, label, state: states[i] }));
  switch (stage) {
    case 'awaiting_funding':  return build(['done', 'current', 'todo', 'todo', 'todo']);
    case 'awaiting_approval': return build(['done', 'done', 'current', 'todo', 'todo']);
    case 'creating':          return build(['done', 'done', 'done', 'current', 'todo']);
    case 'needs_attention':   return build(['done', 'done', 'done', 'blocked', 'todo']);
    case 'created':           return build(['done', 'done', 'done', 'done', 'current']);
    // A reserved vacancy already exists and needs no approval.
    case 'position_reserved': return build(['done', 'done', 'skipped', 'done', 'current']);
    case 'reserved_no_budget': return build(['done', 'current', 'skipped', 'done', 'todo']);
    case 'filled':            return build(['done', 'done', 'done', 'done', 'done']);
    case 'rejected':          return build(['done', 'skipped', 'failed', 'skipped', 'skipped']);
    case 'withdrawn':         return build(['done', 'skipped', 'skipped', 'skipped', 'skipped']);
    default:                  return build(['done', 'todo', 'todo', 'todo', 'todo']);
  }
}

export const isOpenStage = (stage: string): boolean =>
  ['awaiting_funding', 'awaiting_approval', 'creating', 'needs_attention',
    'position_reserved', 'reserved_no_budget'].includes(stage);

const CONTEXT_LABELS: Record<string, string> = {
  adhoc: 'New hire',
  convert: 'Category conversion',
  transfer: 'Transfer',
  reinstate: 'Reinstatement',
};

/** "Category conversion for Sam Example (9990004)", "Bulk hire draft
 *  263, rows 2, 3 and 7". */
export function describeOrigin(origin?: PositionRequestOrigin | null): string {
  if (!origin) return '';
  if (origin.channel === 'bulk_hire') {
    const rows = origin.rows || [];
    const shown = rows.slice(0, 6);
    const extra = rows.length - shown.length;
    const list = extra > 0
      ? `${shown.join(', ')} and ${extra} more`
      : `${shown.slice(0, -1).join(', ')} and ${shown[shown.length - 1]}`;
    const rowText = !rows.length ? '' : rows.length === 1 ? `, row ${rows[0]}` : `, rows ${list}`;
    return `Bulk hire${origin.draft_id ? ` draft ${origin.draft_id}` : ''}${rowText}`;
  }
  if (origin.channel === 'budget_workbench') return 'Budget workbench';
  if (origin.channel === 'role_master') return 'Raised in Role Master';
  if (origin.channel === 'manager') return 'Raised by the hiring manager';
  const what = CONTEXT_LABELS[origin.context || ''] || 'HR Ops';
  const who = origin.worker_name
    ? `${origin.worker_name}${origin.worker_pn ? ` (${origin.worker_pn})` : ''}`
    : origin.worker_pn || '';
  return who ? `${what} for ${who}` : what;
}

export function fundingLabel(f?: TrackedPositionRequest['funding'] | null): string {
  if (!f) return '';
  switch (f.state) {
    case 'none': return 'No budget linked yet';
    case 'partial': return `Budget for ${f.resolved} of ${f.positions}`;
    case 'funded': return 'Budget linked';
    case 'unbudgeted': return 'Signed off without budget';
    case 'mixed': return 'Budget linked, part signed off without';
    default: return '';
  }
}

export const dimsLabel = (x: { campus?: string | null; department?: string | null; faculty?: string | null }): string =>
  [x.campus, x.department, x.faculty].filter((v) => v && String(v).trim()).join(' / ');

/** The appointment a hiring manager asked for, in words. An intern sits on a
 *  fixed-term position, so the request is where HR Ops reads "Intern". */
const APPOINTMENT_LABEL: Record<string, string> = {
  Permanent: 'Permanent',
  FTC: 'Fixed-term',
  ICA: 'Independent contractor (ICA)',
  Intern: 'Intern',
};

export const appointmentLabel = (category?: string | null): string =>
  (category ? APPOINTMENT_LABEL[category] || category : '');

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "1 Oct 2026", on the SAST calendar day. Built from the date's parts
 *  rather than a locale format, whose day padding and month abbreviations
 *  differ between browsers ("01 Oct" / "1 Oct", "Sep" / "Sept"). */
export function formatSastDate(iso?: string | null): string {
  if (!iso) return '';
  const d = new Date(iso.length === 10 ? `${iso}T12:00:00+02:00` : iso);
  if (Number.isNaN(d.getTime())) return iso;
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Africa/Johannesburg', year: 'numeric', month: 'numeric', day: 'numeric',
  }).formatToParts(d);
  const part = (type: string) => Number(parts.find((p) => p.type === type)?.value);
  return `${part('day')} ${MONTHS[part('month') - 1]} ${part('year')}`;
}

export interface SeatFact { text: string; tone: 'good' | 'warn' | 'neutral'; title?: string }

/** What HR needs to know about a vacant position before using it. */
export function seatFacts(seat: ExistingSeat): SeatFact[] {
  const facts: SeatFact[] = [];
  if (seat.reports_to_match === true) {
    facts.push({ text: 'Same manager', tone: 'good' });
  } else if (seat.reports_to_match === false) {
    const who = seat.reports_to_manager || seat.reports_to;
    facts.push(who
      ? { text: `Reports to ${who}`, tone: 'warn', title: 'Not the manager chosen for the new position' }
      : { text: 'No reporting line', tone: 'warn' });
  } else if (seat.reports_to_manager || seat.reports_to) {
    facts.push({ text: `Reports to ${seat.reports_to_manager || seat.reports_to}`, tone: 'neutral' });
  }
  if (seat.funded) {
    facts.push({ text: 'Budget linked', tone: 'good' });
  } else if (seat.held) {
    facts.push({ text: 'Kept for recruitment', tone: 'neutral', title: seat.hold_reason || undefined });
  } else {
    facts.push({ text: 'No budget linked', tone: 'warn' });
  }
  if (seat.job_differs) {
    facts.push({ text: `Coded to job ${seat.job_id}`, tone: 'warn', title: 'Same role title, but the position sits on another job' });
  }
  if (seat.strength === 'near' && !seat.job_differs) {
    facts.push({
      text: seat.faculty_state === 'not_set' ? 'No faculty on the position' : `Faculty ${seat.faculty || '—'}`,
      tone: 'warn',
    });
  }
  if (seat.reserved_for) {
    const r = seat.reserved_for;
    facts.push(r.kind === 'reserved'
      ? {
          text: `Reserved for ${r.request_number}`,
          tone: 'warn',
          title: `Reserved ${formatSastDate(r.created_on)}${r.requested_by ? ` by ${r.requested_by}` : ''} for a hire.`,
        }
      : {
          text: `Created for ${r.request_number}${r.worker_name ? ` (${r.worker_name})` : ''}`,
          tone: 'warn',
          title: `Created ${formatSastDate(r.created_on)}${r.requested_by ? ` for a request by ${r.requested_by}` : ''}. Its hire may still be coming.`,
        });
  }
  if (seat.vacant_since) {
    facts.push({ text: `Vacant since ${formatSastDate(seat.vacant_since)}`, tone: 'neutral' });
  }
  return facts;
}

/** The candidates worth showing up front, and how many sit elsewhere. */
export function splitCandidates(result?: ExistingSeatsResult | null): {
  close: ExistingSeat[];
  elsewhere: ExistingSeat[];
} {
  const all = result?.candidates || [];
  return {
    close: all.filter((c) => c.strength !== 'elsewhere'),
    elsewhere: all.filter((c) => c.strength === 'elsewhere'),
  };
}

/** Same-role positions at the asked campus and department that can't be
 *  used, in a sentence each. */
export function excludedNote(result?: ExistingSeatsResult | null): string {
  const e = result?.excluded;
  if (!e) return '';
  const place = dimsLabel({ campus: result?.asked?.campus, department: result?.asked?.department });
  const where = place ? ` at ${place}` : '';
  const out: string[] = [];
  const n = (e.other_type || 0) + (e.legacy_type || 0) + (e.unknown_type || 0);
  if (n) {
    out.push(`${n} more vacant position${n === 1 ? '' : 's'} of this role${where} ${n === 1 ? 'has' : 'have'} a different `
      + 'position type, so ' + (n === 1 ? 'it' : 'they') + " can't be used for this appointment.");
  }
  const a = e.assigned || 0;
  if (a) {
    out.push(`${a} more position${a === 1 ? '' : 's'} of this role${where} already ${a === 1 ? 'has' : 'have'} someone `
      + 'assigned in F&O (a hire who starts later, or someone not yet on the platform), so '
      + (a === 1 ? "it isn't" : "they aren't") + ' offered.');
  }
  return out.join(' ');
}

/** The warning on a pending request in Role Master, across its groups.
 *  Only exact matches "match", as in the panel it opens; the same role at
 *  another faculty, or coded to another job, is "similar". It read "6 vacant
 *  positions already match" over a panel that said 2 did (UAT, 30 Sep 2026). */
export function matchChipLabel(results: Array<ExistingSeatsResult | null | undefined>): string {
  let exact = 0;
  let near = 0;
  for (const r of results) {
    exact += r?.counts?.exact || 0;
    near += r?.counts?.near || 0;
  }
  if (exact) {
    return `⚠ ${exact} vacant position${exact === 1 ? '' : 's'} already match${exact === 1 ? 'es' : ''}`
      + (near ? ` (+${near} similar)` : '');
  }
  return near ? `⚠ ${near} similar vacant position${near === 1 ? '' : 's'}` : '';
}

/** A short heading for what the check found. */
export function matchHeadline(result?: ExistingSeatsResult | null): string {
  if (!result) return '';
  const { exact, near, elsewhere } = result.counts;
  if (exact) return `${exact} vacant position${exact === 1 ? '' : 's'} already match${exact === 1 ? 'es' : ''} this role here`;
  if (near) return `${near} vacant position${near === 1 ? '' : 's'} of this role at the same campus and department`;
  if (elsewhere) return `${elsewhere} vacant position${elsewhere === 1 ? '' : 's'} of this role elsewhere`;
  return 'No vacant position of this role matches';
}
